import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import { useAuth } from "../../context/AuthContext";
import { fetchAllMyAssessments } from "./clientCourseService";
import "./courses.css";

const CRITERIA = [
  { key: "scoreFluency",     label: "Fluency"     },
  { key: "scoreClarity",     label: "Clarity"     },
  { key: "scoreVocabulary",  label: "Vocabulary"  },
  { key: "scoreGrammar",     label: "Grammar"     },
  { key: "scoreInteraction", label: "Interaction" },
];

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";

const ScoreBar = ({ label, score, compareScore }) => {
  const color = score >= 5 ? "#16a34a" : score >= 4 ? "#65a30d" : score >= 3 ? "#d97706" : score >= 2 ? "#ea580c" : "#dc2626";
  const improved = compareScore != null && score > compareScore;
  const declined = compareScore != null && score < compareScore;
  return (
    <div style={{ marginBottom: "0.5rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.2rem" }}>
        <span style={{ fontSize: "0.78rem", color: "#64748b" }}>{label}</span>
        <span style={{ fontSize: "0.78rem", fontWeight: 700, color }}>
          {score}/5{improved ? " ↑" : declined ? " ↓" : ""}
        </span>
      </div>
      <div style={{ height: 5, background: "#e5e7eb", borderRadius: 3, position: "relative" }}>
        {compareScore != null && (
          <div style={{ position: "absolute", left: 0, top: 0, width: `${(compareScore / 5) * 100}%`, height: "100%", background: "#bfdbfe", borderRadius: 3 }} />
        )}
        <div style={{ position: "absolute", left: 0, top: 0, width: `${(score / 5) * 100}%`, height: "100%", background: color, borderRadius: 3, opacity: 0.85 }} />
      </div>
    </div>
  );
};

const AssessBlock = ({ label, data, compareData, color, bg }) => {
  if (!data) return (
    <div style={{ flex: 1, minWidth: 200, background: "#f9fafb", border: "1px dashed #d1d5db", borderRadius: 10, padding: "1rem", textAlign: "center", color: "#9ca3af", fontSize: "0.82rem" }}>
      <div style={{ fontSize: "1.2rem", marginBottom: "0.3rem" }}>📋</div>
      {label} not yet submitted.
    </div>
  );
  return (
    <div style={{ flex: 1, minWidth: 200, background: bg, border: `1px solid ${color}33`, borderRadius: 10, padding: "1rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
        <span style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color }}>{label}</span>
        <span style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0f172a" }}>
          {data.totalScore}<span style={{ fontSize: "0.7rem", color: "#9ca3af", fontWeight: 400 }}>/25</span>
        </span>
      </div>
      {CRITERIA.map((c) => (
        <ScoreBar key={c.key} label={c.label} score={data[c.key] || 0} compareScore={compareData?.[c.key] ?? null} />
      ))}
      {data.instructorFeedback && (
        <div style={{ marginTop: "0.6rem", background: "#eff6ff", borderRadius: 6, padding: "6px 10px" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#1e40af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>💬 Feedback</div>
          <div style={{ fontSize: "0.8rem", color: "#1e3a8a", fontStyle: "italic" }}>"{data.instructorFeedback}"</div>
        </div>
      )}
      {data.improvementGoals && (
        <div style={{ marginTop: "0.5rem", background: "#fef3c7", borderRadius: 6, padding: "6px 10px" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>🎯 Goals</div>
          <div style={{ fontSize: "0.8rem", color: "#78350f" }}>{data.improvementGoals}</div>
        </div>
      )}
      <div style={{ marginTop: "0.5rem", fontSize: "0.72rem", color: "#9ca3af" }}>
        {data.assessedByName ? `By ${data.assessedByName} · ` : ""}{fmtDate(data.assessedAt)}
      </div>
    </div>
  );
};

const EnrollmentAssessCard = ({ item, onView }) => {
  const { courseName, batchName, initial, finalAssessment, scoreDelta, improved } = item;
  const hasDelta = scoreDelta != null;

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "1.25rem", marginBottom: "1.25rem", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: "1rem", color: "#0f172a" }}>{courseName}</div>
          <div style={{ fontSize: "0.82rem", color: "#6b7280", marginTop: 2 }}>{batchName}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {hasDelta && (
            <div style={{
              background: improved ? "#dcfce7" : "#fee2e2",
              border: `1px solid ${improved ? "#86efac" : "#fca5a5"}`,
              borderRadius: 8, padding: "0.3rem 0.75rem",
              fontSize: "0.85rem", fontWeight: 700,
              color: improved ? "#15803d" : "#b91c1c",
            }}>
              {improved ? `🎉 +${scoreDelta} pts` : `📉 ${scoreDelta} pts`}
            </div>
          )}
          <button className="cc-btn cc-btn-outline" style={{ padding: "6px 14px", fontSize: "0.82rem" }} onClick={onView}>
            View Details →
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <AssessBlock label="Initial Assessment" data={initial}          compareData={null}    color="#2563eb" bg="#eff6ff" />
        <AssessBlock label="Final Assessment"   data={finalAssessment}  compareData={initial} color="#16a34a" bg="#f0fdf4" />
      </div>
    </div>
  );
};

const MyAssessmentsPage = () => {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();

  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?redirect=/my-assessments", { replace: true }); return; }
    fetchAllMyAssessments()
      .then((res) => setItems(Array.isArray(res.data) ? res.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [authLoading, user, navigate]);

  if (authLoading || loading) {
    return (
      <>
        <Navbar />
        <div className="cc-loading"><div className="cc-spinner" /><p>Loading assessments…</p></div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="cc-page" style={{ paddingTop: 24 }}>
        <button
          className="cc-btn cc-btn-outline"
          style={{ marginBottom: 16, padding: "6px 14px", fontSize: "0.82rem" }}
          onClick={() => navigate("/my-courses")}
        >
          ← My Courses
        </button>

        <div className="cc-hero" style={{ marginBottom: 24 }}>
          <h1>My Assessments</h1>
          <p>Track your progress across all enrolled courses</p>
        </div>

        {items.length === 0 ? (
          <div className="cc-empty">
            <div className="cc-empty-icon">📊</div>
            <p>No assessments available yet. Your instructor will submit them after evaluation.</p>
            <button className="cc-btn cc-btn-primary" onClick={() => navigate("/my-courses")}>
              Go to My Courses
            </button>
          </div>
        ) : (
          items.map((item) => (
            <EnrollmentAssessCard
              key={item.enrollmentId}
              item={item}
              onView={() => navigate(`/my-courses/${item.enrollmentId}`)}
            />
          ))
        )}
      </div>
    </>
  );
};

export default MyAssessmentsPage;
